import { Link } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import Reveal from "@/components/Reveal";
import EmberCanvas from "@/components/EmberCanvas";
import StarfieldCanvas from "@/components/StarfieldCanvas";
import HeroGlobe from "@/components/HeroGlobe";
import { TERRITORIA } from "@/data/content";

const VAULT_PLACEHOLDERS = [
  {
    code: "KRONIX",
    status: "CONCEPT PHASE",
    note: "High-energy isometric action MMORPG. Ancient arenas, temporal constructs, exaggerated combat.",
    kronix: true,
    art: "/kronix/key-art.png",
    to: "/games/kronix",
  },
  {
    code: "PROJECT // UNANNOUNCED 03",
    status: "RESEARCH & R&D",
    note: "Proprietary physics and atmospheric simulation testing.",
  },
];

const Games = () => (
  <div className="relative pt-32">
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-[480px]"
      style={{
        background: "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(229,46,46,0.08) 0%, transparent 70%)",
      }}
    />
    <header className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">THE VAULT // PROJECTS</p>
        <h1 className="mt-5 font-display text-5xl font-black uppercase leading-none tracking-tight text-[#F4F5F7] sm:text-7xl lg:text-8xl">
          Our Games
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-[#9EA4B0]">
          One world in the fire. More in the dark. We announce when there is something worth saying.
        </p>
      </Reveal>
    </header>

    <section className="relative mx-auto max-w-[1400px] px-6 py-20 lg:px-10">
      <Reveal>
        <div
          data-testid="games-card-territoria"
          className="group relative overflow-hidden border border-[#282D37] transition-[border-color] duration-500 hover:border-[#E52E2E]/60"
        >
          <div className="relative flex min-h-[420px] items-center justify-center overflow-hidden bg-[#05070C] sm:min-h-[480px]">
            <StarfieldCanvas density={140} className="absolute inset-0 h-full w-full" />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse 55% 55% at 50% 45%, rgba(58,100,190,0.14) 0%, transparent 65%), radial-gradient(ellipse 40% 35% at 50% 100%, rgba(229,46,46,0.08) 0%, transparent 70%)",
              }}
            />
            <div data-testid="games-globe" className="relative flex min-w-0 justify-center">
              <HeroGlobe />
            </div>
          </div>
          <div className="relative flex flex-col gap-6 border-t border-[#282D37] bg-[#0D0F12] p-8 sm:p-12 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="h-2 w-2 animate-ember-pulse rounded-full bg-[#E52E2E]" />
                <span className="font-mono text-[11px] tracking-[0.3em] text-[#E52E2E]">{TERRITORIA.status}</span>
              </div>
              <h2 className="mt-4 font-display text-5xl font-black uppercase leading-none tracking-tight text-white sm:text-7xl">
                {TERRITORIA.title}
              </h2>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#9EA4B0] sm:text-base">
                {TERRITORIA.tagline}
              </p>
            </div>
            <Link
              to="/games/territoria"
              data-testid="games-enter-territoria"
              className="group/btn flex shrink-0 items-center gap-3 border border-[#3A404E] px-8 py-4 font-mono text-xs font-bold tracking-[0.25em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
            >
              ENTER PROJECT
              <ArrowRight size={15} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
            </Link>
          </div>
        </div>
      </Reveal>

      <div className="mt-8 grid gap-6 md:grid-cols-2">
        {VAULT_PLACEHOLDERS.map((p, i) => {
          const Tag = p.to ? Link : "div";
          return (
          <Reveal key={p.code} delay={0.1 * (i + 1)}>
            <Tag
              {...(p.to ? { to: p.to } : {})}
              data-testid={`vault-slot-${i + 2}`}
              className="group relative flex h-full flex-col justify-between overflow-hidden border border-dashed border-[#282D37] bg-[#0D0F12] transition-[border-color] duration-500 hover:border-[#E52E2E]/50"
            >
              {p.art && (
                <div className="relative overflow-hidden">
                  <img
                    src={p.art}
                    alt={`${p.code} key art`}
                    className="aspect-[16/9] w-full object-cover opacity-90 transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0F12] via-transparent to-transparent" />
                </div>
              )}
              {!p.art && (
                <EmberCanvas density={6} className="pointer-events-none absolute inset-0 h-full w-full opacity-30" />
              )}
              <div className={`relative ${p.art ? "p-8 pt-5 sm:p-10 sm:pt-5" : "p-8 sm:p-10"}`}>
                <div className="flex items-center gap-3">
                  <Lock size={13} className="text-[#5F6573]" />
                  <span className="font-mono text-[11px] tracking-[0.3em] text-[#5F6573]">{p.status}</span>
                </div>
                <h3
                  className={`mt-4 font-display font-bold uppercase tracking-wide sm:text-3xl ${
                    p.kronix ? "text-3xl text-[#F4F5F7] sm:text-4xl" : "text-2xl text-[#3A404E]"
                  }`}
                >
                  {p.code}
                </h3>
                <p className="mt-3 text-sm text-[#5F6573]">{p.note}</p>
              </div>
              <p className="relative mt-10 font-mono text-[10px] tracking-[0.35em] text-[#282D37]">
                {p.kronix ? "EARLY CONCEPT // ENTER THE TEASER" : "CLASSIFIED // AWAITING DECLASSIFICATION"}
              </p>
            </Tag>
          </Reveal>
          );
        })}
      </div>
    </section>
  </div>
);

export default Games;
